import { Link } from 'react-router-dom';
import { Badge } from '../atoms/Badge/Badge';
import { Card } from '../atoms/Card/Card';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import {
  EntryPhotoPreview,
  ENTRY_PHOTO_BODY_CLASS,
  ENTRY_PHOTO_HOST_CLASS,
  entryPhotoSrc,
} from '../media/EntryPhotoPreview/EntryPhotoPreview';
import { journalTagLabel } from '../journal/journalTags';
import { toPlainParagraphs, type CommunityEntry } from '../../services/communityService';
import { useTranslation } from '../../i18n/useTranslation';
import './CommunityFeedItem.css';

const PREVIEW_LENGTH = 220;

export function formatEntryDate(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language, { dateStyle: 'long' }).format(date);
}

function preview(content: string): string {
  const text = toPlainParagraphs(content).join(' ');
  return text.length <= PREVIEW_LENGTH ? text : `${text.slice(0, PREVIEW_LENGTH).trimEnd()}…`;
}

/**
 * One public journal entry in the Community feed. The author is never named
 * (no public display names exist): "community member" plus the aquarium name.
 * Attached public photos appear exactly like the journal list's entry preview
 * (the shared EntryPhotoPreview: the newest photo, "+N" for the others).
 */
export function CommunityFeedItem({ entry }: { entry: CommunityEntry }) {
  const { t, i18n } = useTranslation();
  const text = preview(entry.content);
  const photo = entry.attachedImages?.[0] ?? null;
  const hasPhoto = entryPhotoSrc(photo) !== null;
  const photoCount = Math.max(entry.attachedImageCount ?? 0, entry.attachedImages?.length ?? 0);
  return (
    <Card
      as="article"
      className={`rg-community-item${hasPhoto ? ` ${ENTRY_PHOTO_HOST_CLASS}` : ''}`}
      data-testid="community-feed-item"
    >
      {photo && hasPhoto && (
        <EntryPhotoPreview
          image={photo}
          count={photoCount}
          countLabel={t('community.photosAttached', { count: photoCount })}
          data-testid="community-feed-item-photo"
        />
      )}
      <div className={`rg-community-item__body ${ENTRY_PHOTO_BODY_CLASS}`}>
        <div className="rg-community-item__meta">
          <Text as="span" size="sm" color="secondary">
            {entry.isOwn ? t('community.yourEntry') : t('community.byMember')}
            {' · '}
            {t('community.aquarium', { name: entry.aquariumName || t('community.unnamedAquarium') })}
          </Text>
          <Text as="span" size="sm" color="muted">
            <time dateTime={entry.createdAt}>{formatEntryDate(entry.createdAt, i18n.language)}</time>
          </Text>
        </div>
        <Heading level={2} variant="card" className="rg-community-item__title">
          <Link className="rg-community-item__link" to={`/community/${entry.id}`}>
            {entry.title}
          </Link>
        </Heading>
        {text && <Text className="rg-community-item__preview">{text}</Text>}
        {entry.tags.length > 0 && (
          <ul className="rg-community-item__tags" aria-label={t('journal.tags')}>
            {entry.tags.map((tag) => (
              <li key={tag}><Badge tone="neutral">{journalTagLabel(tag, t)}</Badge></li>
            ))}
          </ul>
        )}
        <Text as="span" size="sm" color="secondary" className="rg-community-item__counts">
          {t('community.likesCount', { count: entry.likeCount })}
          {' · '}
          {t('community.commentsCount', { count: entry.commentCount })}
        </Text>
      </div>
    </Card>
  );
}

export default CommunityFeedItem;
